/**
 * Client "Leave a testimonial" form. Submits to POST /api/testimonials as
 * 'pending'; the coach reviews, trims to a short quote, and approves it for the
 * marketing site.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Star, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';

export default function Testimonial() {
  const navigate = useNavigate();
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit() {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post('/api/testimonials', { body: text.trim(), rating });
      setSent(true);
    } catch {
      /* swallow — keep it simple; user can retry */
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-6">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--gold)' }}>
          <ArrowLeft size={22} />
        </button>
        <div>
          <h1 className="display" style={{ fontSize: '1.9rem' }}>Testimonial</h1>
          <p className="text-sm" style={{ color: 'var(--gray-400)' }}>Share your experience</p>
        </div>
      </div>

      {sent ? (
        <div className="card flex flex-col items-center text-center gap-3 py-10">
          <CheckCircle2 size={48} style={{ color: 'var(--success, #22C55E)' }} />
          <p className="display" style={{ fontSize: '1.4rem' }}>Thank you!</p>
          <p className="text-sm" style={{ color: 'var(--gray-400)' }}>
            Your testimonial was sent to your coach for review.
          </p>
          <button className="btn-gold mt-2" onClick={() => navigate('/profile')}>Done</button>
        </div>
      ) : (
        <>
          <div className="card flex flex-col gap-4">
            <div>
              <label className="block text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--gray-400)' }}>Rating</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} onClick={() => setRating(i)} type="button">
                    <Star size={26} fill={i <= rating ? 'var(--gold)' : 'none'} style={{ color: 'var(--gold)' }} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--gray-400)' }}>Your testimonial</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                placeholder="What's your experience been like?"
                className="input w-full resize-y"
              />
            </div>
            <p className="text-xs" style={{ color: 'var(--gray-500)' }}>
              Your coach may shorten it to a quote before it appears publicly.
            </p>
          </div>
          <button
            className="btn-gold w-full mt-4"
            disabled={saving || !text.trim()}
            onClick={submit}
          >
            {saving ? 'Sending…' : 'Submit'}
          </button>
        </>
      )}
    </div>
  );
}
